import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import { fetchJobById } from "../../Redux/ActionCreators/jobsAction";
import { fetchIdibuStatus } from "../../Redux/ActionCreators/idibuAction";
import { useDispatch, useSelector } from "react-redux";
import { AutomationContext } from "./AutomationContext";
import "./jobsV2.scss";
import { useAppPermission } from "../../hooks/useAppPermission";
import { P } from "../../constants/rbac";

const JobTabsV2 = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { jobId } = useParams();
  const { can } = useAppPermission();
  const canViewCandidates = can(P.JOBS_CANDIDATE_READ);
  const canInviteCandidates = can(P.JOBS_CANDIDATE_CREATE);
  const canViewSettings = can(P.JOBS_SETTINGS_READ) || can(P.JOBS_SETTINGS_EDIT) || can(P.JOBS_JOB_EDIT);
  const canViewAutomation = can(P.JOBS_AUTOMATION_READ) || can(P.JOBS_AUTOMATION_EDIT) || can(P.JOBS_JOB_EDIT);
  const canEditAutomation = can(P.JOBS_AUTOMATION_EDIT) || can(P.JOBS_JOB_EDIT);

  const job = useSelector((state) => state.jobs.latestJob);
  const totalCandidates = useSelector((state) => state.candidates.pagination?.total);

  const [titleLoading, setTitleLoading] = useState(false);
  const [automationControls, setAutomationControls] = useState(null);
  const [idibuEnabled, setIdibuEnabled] = useState(false);

  const pathParts = location.pathname.split("/");
  const isAutomationTab = pathParts[pathParts.length - 1] === "automation";
  const isCandidatesTab = pathParts[pathParts.length - 1] === "candidates";

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("CLOSE_SIDEBAR"));
  }, []);

  useEffect(() => {
    if (!canViewCandidates && isCandidatesTab && jobId) {
      navigate(`/jobs/${jobId}/details`, { replace: true });
    }
  }, [canViewCandidates, isCandidatesTab, jobId, navigate]);

  useEffect(() => {
    const fetchJobData = async () => {
      if (jobId) {
        setTitleLoading(true);
        await dispatch(fetchJobById(jobId));
        setTitleLoading(false);
      }
    };
    fetchJobData();
  }, [dispatch, jobId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const status = await dispatch(fetchIdibuStatus());
      if (mounted) {
        setIdibuEnabled(Boolean(status?.effective_enabled));
      }
    })();
    return () => {
      mounted = false;
    };
  }, [dispatch, jobId]);

  const formatPostedDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  const buildSubtitle = () => {
    const parts = [];
    const companyName = job?.company_name || job?.organization?.name || job?.company || "";
    if (companyName) parts.push(companyName);
    if (job?.created_at) parts.push(`Posted ${formatPostedDate(job.created_at)}`);
    if (totalCandidates != null) parts.push(`${totalCandidates} candidates`);
    return parts.join(" · ");
  };

  const subtitle = job ? buildSubtitle() : "";

  return (
    <AutomationContext.Provider value={{ controls: automationControls, setControls: setAutomationControls }}>
      <div className="job-tabs-v2">
        <div className="job-navigation-v2">
          <div className="job-nav-left">
            <button className="back-arrow-v2" onClick={() => navigate("/jobs")}>
              <img src="/images/back-arrow-black.svg" alt="Back" width={20} height={20} />
            </button>
            <div className="job-title-block">
              {titleLoading ? (
                <div className="spinner-border spinner-border-sm" role="status" style={{ color: "#F75891" }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <>
                  <h3 className="job-title-v2">{job ? job.title : ""}</h3>
                  {subtitle && <p className="job-subtitle-v2">{subtitle}</p>}
                </>
              )}
            </div>
          </div>

          {isAutomationTab ? (
            <div className="auto-header-controls">
              {canEditAutomation && automationControls?.hasAutomation && (
                <div className="auto-toggle-wrap">
                  <span className="auto-toggle-label auto-toggle-label--pre">Automation</span>
                  <button
                    className={`auto-toggle-btn${automationControls.isActive ? " on" : ""}`}
                    onClick={automationControls.onToggle}
                    aria-label="Toggle automation"
                  />
                  <span className="auto-toggle-label">
                    {automationControls.isActive ? "On" : "Off"}
                  </span>
                </div>
              )}
              {!canEditAutomation && isAutomationTab && (
                <span className="auto-toggle-label" style={{ color: '#8a9199', fontSize: 12 }}>
                  View only
                </span>
              )}
              {automationControls?.showControls && (
                <>
                  <button className="auto-ctrl-btn" onClick={automationControls?.onFit}>Fit</button>
                  {canEditAutomation && (
                  <button className="auto-ctrl-btn" onClick={automationControls?.onReset}>Reset</button>
                  )}
                  {canEditAutomation && (
                  <button
                    className="auto-ctrl-btn auto-ctrl-btn--primary"
                    onClick={automationControls?.onSave}
                    disabled={automationControls?.saving || !automationControls?.hasChanges}
                  >
                    {automationControls?.saving ? "Saving…" : "Save"}
                  </button>
                  )}
                </>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {canInviteCandidates && (
              <button
                className="add-candidate-btn-v2"
                onClick={() => navigate(`/jobs/${jobId}/invite`)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <line x1="19" y1="8" x2="19" y2="14" />
                  <line x1="22" y1="11" x2="16" y2="11" />
                </svg>
                Invite Candidates
              </button>
              )}
            </div>
          )}
        </div>

        <div className="tabs-header-v2">
          <nav className="tabs-nav-v2">
            {canViewCandidates && (
              <>
                <NavLink
                  to={`/jobs/${jobId}/insights`}
                  className={({ isActive }) => `tab-btn-v2 ${isActive ? "active" : ""}`}
                >
                  Overview
                </NavLink>
                <NavLink
                  to={`/jobs/${jobId}/candidates`}
                  className={({ isActive }) => `tab-btn-v2 ${isActive ? "active" : ""}`}
                >
                  Candidates
                </NavLink>
              </>
            )}
            <NavLink
              to={`/jobs/${jobId}/details`}
              className={({ isActive }) => `tab-btn-v2 ${isActive ? "active" : ""}`}
            >
              Job Details
            </NavLink>
            {canViewSettings && (
            <NavLink
              to={`/jobs/${jobId}/settings`}
              className={({ isActive }) => `tab-btn-v2 ${isActive ? "active" : ""}`}
            >
              Job Settings
            </NavLink>
            )}
            {canViewAutomation && (
            <NavLink
              to={`/jobs/${jobId}/automation`}
              className={({ isActive }) => `tab-btn-v2 ${isActive ? "active" : ""}`}
            >
              Automation
            </NavLink>
            )}
            {idibuEnabled ? (
              <NavLink
                to={`/jobs/${jobId}/job-boards`}
                className={({ isActive }) => `tab-btn-v2 ${isActive ? "active" : ""}`}
              >
                Post to Job Boards
              </NavLink>
            ) : null}
          </nav>
        </div>

        <div className="tab-content-v2">
          <Outlet />
        </div>
      </div>
    </AutomationContext.Provider>
  );
};

export default JobTabsV2;
